/**
 * Every user-visible string in the admin application.
 *
 * A sibling of apps/web/src/i18n/de.ts, not an import of it — the two
 * applications ship independently and are meant to read as the same
 * product, not to share a runtime dependency for their copy. Consumed by
 * direct property access (`de.auth.login.title`), so a missing or
 * misspelled key is a compile error rather than a string that renders as
 * its own key.
 */
import { formatCount } from './format'

export const de = {
  app: {
    name: 'BusinessBuddy',
    /** Distinguishes this tab from the customer application when both are
        open at once — see index.html's <title>. */
    suiteName: 'Verwaltung',
  },

  auth: {
    login: {
      title: 'Anmelden',
      subtitle: 'Verwaltung von BusinessBuddy — für den Betreiber.',
      email: 'E-Mail',
      password: 'Passwort',
      submit: 'Anmelden',
      /**
       * Design §3: a login without administratorrecht is refused with the
       * exact same message as a wrong password. Whoever finds this address
       * must not learn from the rejection which accounts are
       * administrators — so this string is used for both cases, and no
       * second, more specific string exists anywhere in this file for the
       * non-admin case.
       */
      failed: 'E-Mail oder Passwort stimmt nicht.',
      rateLimited: 'Zu viele Versuche. Warten Sie einen Moment.',
    },
    logout: 'Abmelden',
  },

  theme: {
    label: 'Erscheinungsbild',
    light: 'Hell',
    dark: 'Dunkel',
    system: 'System',
  },

  nav: {
    dashboard: 'Übersicht',
    users: 'Benutzer',
    settings: 'Einstellungen',
    openMenu: 'Menü öffnen',
    closeMenu: 'Menü schließen',
  },

  common: {
    cancel: 'Abbrechen',
    close: 'Schließen',
    search: 'Suchen',
    filter: 'Filter',
    all: 'Alle',
    clear: 'Zurücksetzen',
    unknownError: 'Etwas ist schiefgelaufen. Bitte erneut versuchen.',
    never: 'Nie',
    none: 'Keine',
  },

  dashboard: {
    title: 'Übersicht',
    sections: {
      accounts: 'Konten',
      companies: 'Firmen',
      volume: 'Datenvolumen',
      dataSources: 'Datenquellen',
      managedMailboxes: 'Verwaltete Postfächer',
      storage: 'Ablageverzeichnis',
    },
    accounts: {
      total: 'Konten gesamt',
      newLast30Days: 'Neu in 30 Tagen',
      activeLast30Days: 'Aktive Konten (30 Tage)',
    },
    companies: {
      total: 'Firmen gesamt',
    },
    volume: {
      transactions: 'Transaktionen',
      documents: 'Belege',
      invoices: 'Rechnungen',
    },
    dataSources: {
      /** Per data-source type: "3 Quellen, davon 1 fehlerhaft." */
      failing: (failing: number) => `davon ${formatCount(failing)} fehlerhaft`,
      noneFailing: 'keine fehlerhaft',
      empty: 'Noch keine Datenquelle auf dieser Installation.',
      source: 'Quelle',
      sources: 'Quellen',
      type: {
        email: 'E-Mail-Postfach',
        stripe: 'Stripe',
      },
    },
    managedMailboxes: {
      provisioned: 'Eingerichtet',
      missing: 'Fehlend',
    },
    storage: {
      documentBytes: 'Belegung',
    },
  },

  users: {
    title: 'Benutzer',
    empty: {
      title: 'Noch kein Benutzer',
      description: 'Sobald sich jemand registriert, erscheint das Konto hier.',
    },
    noResults: {
      title: 'Keine Treffer',
      description: 'Kein Konto entspricht der Suche oder dem gesetzten Filter.',
    },
    search: 'Name, E-Mail oder Firma durchsuchen',
    columns: {
      name: 'Name',
      email: 'E-Mail',
      company: 'Firma',
      role: 'Rolle',
      createdAt: 'Registriert am',
      lastActiveAt: 'Zuletzt aktiv',
      isAdmin: 'Administrator',
      actions: 'Aktionen',
    },
    role: {
      owner: 'Inhaber',
      member: 'Mitglied',
    },
    adminFilter: {
      onlyAdmins: 'Nur Administratoren',
      onlyNonAdmins: 'Ohne Administratorrecht',
    },
    yes: 'Ja',
    no: 'Nein',
    rowMenu: {
      open: 'Aktionen öffnen',
      viewDetails: 'Details anzeigen',
      deleteAccount: 'Konto löschen',
    },
    detail: {
      title: 'Kontodetails',
      person: 'Person',
      company: 'Firma',
      usage: 'Nutzung',
      dataSources: 'Datenquellen',
      managedMailbox: 'Verwaltetes Postfach',
      registeredAt: 'Registriert am',
      vatId: 'USt-IdNr.',
      taxNumber: 'Steuernummer',
      invoiceNumberPattern: 'Rechnungsnummernkreis',
      otherMembers: 'Weitere Mitglieder',
      lastActiveAt: 'Letzte Aktivität',
      noMailbox: 'Kein Postfach eingerichtet',
      noDataSources: 'Keine Datenquelle eingerichtet.',
      lastRun: 'Letzter Lauf',
      lastError: 'Letzter Fehler',
      never: 'Noch nie gelaufen',
      status: {
        running: 'Läuft',
        success: 'Erfolgreich',
        error: 'Fehlgeschlagen',
      },
      enabled: 'Aktiv',
      disabled: 'Aus',
      managed: 'Verwaltet',
    },
    /**
     * §7's confirmation. Every sentence names what will be destroyed, with
     * counts, and says which of the two deletions (§6.0) applies —
     * building the exact sentence here, not in the component, is what
     * keeps a dynamic count out of a .tsx file.
     */
    delete: {
      title: 'Konto löschen',
      confirmLabel: 'Konto endgültig löschen',
      typeEmailLabel: 'Zur Bestätigung die E-Mail-Adresse eingeben',
      typeEmailPlaceholder: 'name@beispiel.de',
      companySentence: (name: string, email: string, transactions: number, documents: number, invoices: number) =>
        `Löscht die Firma von ${name} (${email}) mit ${formatCount(transactions)} Transaktionen, ` +
        `${formatCount(documents)} Belegen und ${formatCount(invoices)} Rechnungen. ` +
        `Nicht rückgängig zu machen.`,
      userSentence: (name: string, email: string) =>
        `Entfernt nur das Konto von ${name} (${email}). Firma, Kollegen und Buchungen bleiben erhalten. ` +
        `Nicht rückgängig zu machen.`,
      successCompany: (name: string) => `Firma von ${name} gelöscht.`,
      successUser: (name: string) => `Konto von ${name} entfernt.`,
    },
  },

  /**
   * §8. `hasValue`/`isSecret` from `SettingListItem` (apps/api's
   * settings/service.ts) drive what the "Wert" column shows — see
   * settingsForm.ts's `settingValueDisplay`. `validity.*` labels the
   * currently-resolved row's window (`validFrom`/`validTo`, both on the
   * wire, read-only) — see `settingValidityLabel` — shown only on a row
   * that actually has one; `intro` still says this view has no control to
   * *set* a window, which stays true.
   */
  settings: {
    title: 'Einstellungen',
    intro: 'Zugangsdaten, Grenzwerte und Frontend-Schalter dieser Installation. Diese Übersicht zeigt den ' +
      'aktuell wirksamen Wert; ein befristet hinterlegter Wert für später wird hier nicht angezeigt.',
    empty: {
      title: 'Keine Einstellung angelegt',
      description: 'Die Startbestückung fehlt noch auf dieser Installation.',
    },
    columns: {
      name: 'Schlüssel',
      description: 'Beschreibung',
      value: 'Wert',
      forFrontend: 'Fürs Frontend',
    },
    value: {
      stored: 'hinterlegt',
      unset: 'nicht gesetzt',
    },
    /** §8.2's validity window, for the "Wert"-adjacent hint shown only on a row that has one (see settingValidityLabel). */
    validity: {
      from: (date: string) => `Gültig ab ${date}`,
      until: (date: string) => `Gültig bis ${date}`,
      range: (from: string, until: string) => `Gültig ${from} – ${until}`,
    },
    yes: 'Ja',
    no: 'Nein',
    rowMenu: {
      open: 'Aktionen öffnen',
      edit: 'Bearbeiten',
    },
    form: {
      value: 'Wert',
      newSecretValue: 'Neuer Wert',
      secretHint: 'Wird nicht angezeigt. Leer lassen, um den hinterlegten Wert zu behalten.',
      newSecretPlaceholder: '••••••••',
      description: 'Beschreibung',
      forFrontend: 'Ans Frontend ausliefern',
      forFrontendBlocked: 'Eine geheime Einstellung kann nicht ans Frontend ausgeliefert werden — ' +
        'das hieße, das Geheimnis an jeden Browser zu senden.',
      clearSecretHint: 'Ein hinterlegter Wert bleibt bestehen, bis er ausdrücklich entfernt wird.',
      clearSecretButton: 'Hinterlegten Wert entfernen',
      save: 'Speichern',
    },
    /**
     * §7's confirmation, built by settingsForm.ts's `settingConfirmationLines`
     * from exactly the fields about to be written. A secret's new value is
     * never part of it — `newSecretValue` says a value is being stored,
     * never what it is.
     */
    confirm: {
      heading: (name: string) => `Diese Änderung an „${name}“ wird gespeichert:`,
      newValue: (value: string) => value === '' ? 'Wert wird geleert.' : `Wert wird auf „${value}“ gesetzt.`,
      newSecretValue: 'Ein neuer Wert wird hinterlegt. Er wird hier nie angezeigt.',
      newDescription: (description: string) =>
        description === '' ? 'Beschreibung wird geleert.' : `Beschreibung wird auf „${description}“ gesetzt.`,
      forFrontendOn: 'Wird künftig ans Frontend ausgeliefert.',
      forFrontendOff: 'Wird nicht mehr ans Frontend ausgeliefert.',
      confirmLabel: 'Änderung bestätigen',
      clearSecretHeading: (name: string) => `Der hinterlegte Wert von „${name}“ wird entfernt.`,
      clearSecretDetail: 'Die Einstellung gilt danach als nicht gesetzt, bis ein neuer Wert eingetragen wird.',
      confirmClearLabel: 'Wert entfernen',
    },
    toast: {
      saved: (name: string) => `„${name}“ gespeichert.`,
      cleared: (name: string) => `Hinterlegter Wert von „${name}“ entfernt.`,
    },
  },
} as const

export type Dictionary = typeof de
